import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Banknote,
  FileText,
  History,
  Info,
  Truck,
  User,
  UserCheck,
  ChevronLeft,
  Package,
  CalendarDays,
  Hourglass,
  Pencil,
  RefreshCw,
  Loader,
  Check,
  Landmark,
  AlertTriangle,
  X,
  XCircle,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { adminApi, fileUrl } from '../../api/client';
import AssignTranslatorForm from '../../components/dashboard/AssignTranslatorForm';
import DashModal from '../../components/dashboard/DashModal';
import StatusBadge from '../../components/dashboard/StatusBadge';
import './AdminOrders.css';
import './AdminHome.css';

const HISTORY_LABELS = {
  created: 'Commande créée',
  payment_received: 'Paiement reçu',
  payment_failed: 'Paiement échoué',
  matching_proposal: 'Matching automatique',
  assigned: 'Professionnel affecté',
  unassigned: 'Professionnel désaffecté',
  status_changed: 'Statut modifié',
  mission_assigned: 'Mission envoyée',
  mission_accepted: 'Mission acceptée',
  mission_refused: 'Mission refusée',
  mission_in_progress: 'Traduction commencée',
  mission_ready: 'Document déposé',
  mission_delivered: 'Document livré',
  mission_cancelled: 'Mission annulée',
  document_uploaded: 'Document déposé',
  document_added: 'Document ajouté',
  delivery_updated: 'Livraison mise à jour',
  verifier_assigned: 'Vérificateur affecté',
};

const DELIVERY_MODE_LABELS = {
  online: 'En ligne',
  email: 'Email',
  pickup: 'Retrait sur place',
  post: 'La Poste',
  express: 'Livraison rapide',
};

const DOC_KIND_LABELS = { source: 'Source', draft: 'Brouillon', final: 'Final' };

const PRIORITY_LABELS = { normal: 'Normal', high: 'Rapide', urgent: 'Urgent' };

const CLOSED_STATUSES = ['delivered', 'completed', 'cancelled', 'refused', 'payment_failed', 'quote_expired'];

const CANCELLED_STATUSES = ['cancelled', 'refused', 'payment_failed', 'quote_expired'];

const STEP_LABELS = [
  'Commande créée', 'Professionnel affecté', 'Paiement',
  'Traduction en cours', 'Vérification', 'Livraison', 'Terminée',
];

const STEP_FOR_STATUS = {
  draft: 0, request_sent: 0, pending_review: 0, quote_sent: 0, quote_accepted: 0, pending: 0,
  translator_to_assign: 1, translator_unavailable: 1, translator_assigned: 1, assigned: 1,
  payment_pending: 2, paid: 2,
  translation_in_progress: 3, in_progress: 3, translation_submitted: 3,
  verification_requested: 4, lawyer_notary_in_progress: 4, correction_requested: 4,
  new_version: 4, verification_validated: 4, final_control: 4,
  ready_to_deliver: 5, ready: 5, delivered: 6, completed: 7,
};

function deliveryStatus(order) {
  const di = order.deliveryInfo || {};
  if (di.deliveredAt) return 'Livrée';
  if (di.sentAt) return 'Envoyée';
  return 'En attente';
}

function Row({ label, children }) {
  return (
    <div className="od-row">
      <span className="od-label">{label}</span>
      <span className="od-value">{children}</span>
    </div>
  );
}

export default function AdminOrderDetailPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const d = t.dashboard;
  const a = d.admin;
  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payVerify, setPayVerify] = useState(null); // { type: 'confirm'|'reject', payment }
  const [payBusy, setPayBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editForm, setEditForm] = useState({ price: '', deadline: '', adminNotes: '' });
  const [statusForm, setStatusForm] = useState('');
  const [msg, setMsg] = useState('');

  const reload = async () => {
    const res = await adminApi.order(id);
    const o = res.order;
    setOrder(o);
    setPayments(res.payments || []);
    setEditForm({
      price: o?.price ?? '',
      deadline: o?.deadline || '',
      adminNotes: o?.adminNotes || '',
    });
    setStatusForm(o?.status || '');
  };

  useEffect(() => {
    reload().catch(() => setOrder(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!order) return <p className="dash-empty">{d.empty}</p>;

  const details = order.details || {};
  const contact = order.contact || {};
  const breakdown = details.pricingBreakdown || null;
  const deliveryFee = order.deliveryInfo?.fee;
  const isInterpreting = order.category === 'interpreting';
  const clientName = [contact.civility, contact.firstName, contact.lastName].filter(Boolean).join(' ') || order.client || '—';
  const closed = CLOSED_STATUSES.includes(order.status);

  const saveEdit = async () => {
    setMsg('');
    try {
      await adminApi.updateOrderMeta(id, {
        price: editForm.price,
        deadline: editForm.deadline || null,
        adminNotes: editForm.adminNotes,
      });
      await reload();
      setEditOpen(false);
      setMsg('Commande mise à jour');
    } catch {
      setMsg('Error');
    }
  };

  const saveStatus = async () => {
    setMsg('');
    try {
      await adminApi.updateOrderMeta(id, { status: statusForm });
      await reload();
      setStatusOpen(false);
      setMsg('Statut mis à jour');
    } catch {
      setMsg('Error');
    }
  };

  const unassign = async () => {
    setMsg('');
    try {
      await adminApi.unassignOrder(id);
      await reload();
    } catch {
      setMsg('Error');
    }
  };

  const pendingTransfers = payments.filter(
    (p) => p.status === 'pending' && ['transfer', 'virement', 'cash'].includes(p.method)
  );

  const runPayVerify = async () => {
    if (!payVerify) return;
    setPayBusy(true);
    try {
      if (payVerify.type === 'confirm') await adminApi.confirmPayment(payVerify.payment._id);
      else await adminApi.rejectPayment(payVerify.payment._id);
      setPayVerify(null);
      setMsg(payVerify.type === 'confirm' ? 'Paiement confirmé' : 'Paiement marqué non reçu');
      await reload();
    } catch {
      setMsg('Error');
    } finally {
      setPayBusy(false);
    }
  };

  return (
    <div className="ah">
      <Link to="/admin/orders" className="dash-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <ChevronLeft size={15} />{a.ordersTitle}
      </Link>

      <header className="ah-hero">
        <div className="ah-hero-text">
          <span className="ah-hero-eyebrow">
            <Package size={13} />
            {isInterpreting ? 'Interprétariat' : 'Traduction'}
          </span>
          <h1>Commande {order.id}</h1>
          <p>{order.document} · {order.sourceLang} → {order.targetLang}</p>
          <div className="ord-hero-meta">
            <span><CalendarDays size={13} />{order.date || '—'}</span>
            {order.deadline ? <span><Hourglass size={13} />{order.deadline}</span> : null}
            <StatusBadge status={order.status} labels={d.status} />
          </div>
        </div>
        <div className="ah-hero-actions">
          <div className="ord-hero-price">
            <em>{a.colTotal || 'Total'}</em>
            <strong>{order.price != null ? `${order.price} ${order.currency || 'TND'}` : '—'}</strong>
          </div>
          {!closed && (
            <button type="button" className="ah-hero-btn ah-hero-btn--gold" onClick={() => setAssignOpen(true)}>
              <UserCheck size={16} />{order.translator ? 'Changer le professionnel' : 'Assigner un professionnel'}
            </button>
          )}
          {!closed && (
            <button type="button" className="ah-hero-btn ah-hero-btn--ghost" onClick={() => { setEditOpen(true); setStatusOpen(false); }}>
              <Pencil size={15} />Modifier
            </button>
          )}
          <button type="button" className="ah-hero-btn ah-hero-btn--ghost" onClick={() => { setStatusOpen(true); setEditOpen(false); }}>
            <RefreshCw size={15} />Changer statut
          </button>
        </div>
      </header>

      <DashModal open={editOpen} title={`Modifier — ${order.id}`} onClose={() => setEditOpen(false)}>
        <div className="dash-form">
          <div className="dash-field">
            <label>{a.colTotal} (TND)</label>
            <input type="number" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} />
          </div>
          <div className="dash-field">
            <label>Date limite</label>
            <input type="date" value={editForm.deadline} onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })} />
          </div>
          <div className="dash-field">
            <label>{a.adminNotes}</label>
            <textarea rows={3} value={editForm.adminNotes} onChange={(e) => setEditForm({ ...editForm, adminNotes: e.target.value })} />
          </div>
          <button type="button" className="btn btn-primary" onClick={saveEdit}>{d.save}</button>
        </div>
      </DashModal>

      <DashModal open={statusOpen} title={`Changer statut — ${order.id}`} onClose={() => setStatusOpen(false)}>
        <div className="dash-form">
          <div className="dash-field">
            <label>Statut</label>
            <select value={statusForm} onChange={(e) => setStatusForm(e.target.value)}>
              {['draft','request_sent','pending_review','quote_sent','quote_accepted','payment_pending','paid','translator_to_assign','translator_assigned','translation_in_progress','translation_submitted','verification_requested','lawyer_notary_in_progress','correction_requested','new_version','verification_validated','final_control','ready_to_deliver','delivered','completed','refused','cancelled','payment_failed','quote_expired','translator_unavailable','pending','assigned','in_progress','ready'].map((s) => (
                <option key={s} value={s}>{d.status[s] || s}</option>
              ))}
            </select>
          </div>
          <button type="button" className="btn btn-primary" onClick={saveStatus}>{d.save}</button>
        </div>
      </DashModal>

      <div className="ah-grid">
        <div className="ah-col">
          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Info size={16} /></span>
              <h2>Informations de la commande</h2>
            </div>
            <div className="ah-card-body">
            <dl className="od-dl">
              <Row label="Prestation">{isInterpreting ? 'Interprétariat' : 'Traduction'}</Row>
              <Row label="Service">{details.translationService || details.interpretingType || '—'}</Row>
              <Row label="Langue source">{order.sourceLang}</Row>
              <Row label="Langue cible">{order.targetLang}</Row>
              {!isInterpreting && (
                <Row label="Volume détecté">
                  {details.detectedPages || details.detectedBillablePages
                    ? `${details.detectedPages || details.detectedBillablePages} page(s) · ${Number(details.detectedWords || 0).toLocaleString('fr-FR')} mots`
                    : `${details.pagesEstimate || order.pages || '—'}${details.words ? ` · ${details.words} mots` : ''}`}
                </Row>
              )}
              {!isInterpreting && Array.isArray(details.fileDocTypes) && details.fileDocTypes.some((f) => f.wordsNeedReview) ? (
                <Row label="Comptage">Vérification manuelle requise (document scanné ou image)</Row>
              ) : null}
              {!isInterpreting && details.translationType ? (
                <Row label="Type de traduction">{{ professional: 'Professionnelle', sworn: 'Assermentée', certified: 'Certifiée' }[details.translationType] || details.translationType}</Row>
              ) : null}
              {!isInterpreting && details.formality && details.formality !== 'none' ? (
                <Row label="Formalité complémentaire">
                  {{ legalization: 'Légalisation', apostille: 'Apostille', unsure: 'À vérifier par l’équipe' }[details.formality] || details.formality}
                </Row>
              ) : null}
              {!isInterpreting && details.useCountry ? (
                <Row label="Pays d'utilisation">{details.useCountry === 'unknown' ? 'Non précisé (à confirmer)' : details.useCountry}</Row>
              ) : null}
              {!isInterpreting && details.originalDoc ? (
                <Row label="Document original">{{ yes: 'Disponible', no: 'Non disponible', unknown: 'À confirmer' }[details.originalDoc] || details.originalDoc}</Row>
              ) : null}
              {!isInterpreting && details.recipientOrg ? (
                <Row label="Organisme destinataire">{details.recipientOrg}</Row>
              ) : null}
              {!isInterpreting && details.deliveryDate ? (
                <Row label="Échéance souhaitée">
                  {details.deliveryDate} · Avant {details.deliveryTime || '—'}{details.deadlineFlex === 'flexible' ? ' · flexible' : ' · impérative'}
                </Row>
              ) : null}
              {Array.isArray(details.fileDocTypes) && details.fileDocTypes.length > 1 ? (
                <Row label="Types par fichier">{details.fileDocTypes.map((f) => `${f.name} → ${f.docType}`).join(' · ')}</Row>
              ) : null}
              {details.pricePending ? <Row label="Tarification">Prix à valider avant paiement</Row> : null}
              {isInterpreting && details.interpretingType ? <Row label="Type de mission">{details.interpretingType}</Row> : null}
              {isInterpreting && details.interpretingMode ? <Row label="Mode d'intervention">{details.interpretingMode}</Row> : null}
              {isInterpreting && details.missionDate ? (
                <Row label={details.missionEndDate ? 'Dates de la mission' : 'Date de mission'}>
                  {details.missionDate}
                  {details.missionEndDate ? ` → ${details.missionEndDate}` : ''}
                  {details.missionStartTime ? ` à ${details.missionStartTime}` : ''}
                </Row>
              ) : null}
              {isInterpreting && details.duration ? (
                <Row label="Durée">{details.duration} h{details.missionEndDate ? ' / jour' : ''}</Row>
              ) : null}
              {isInterpreting && (details.missionCity || details.missionAddress) ? (
                <Row label="Lieu">{[details.missionAddress, details.missionCity].filter(Boolean).join(', ')}</Row>
              ) : null}
              {isInterpreting && details.meetingLink ? <Row label="Visioconférence">{details.meetingLink}</Row> : null}
              {isInterpreting && details.participants ? <Row label="Participants">{details.participants}</Row> : null}
              {isInterpreting && details.subject ? <Row label="Sujet / contexte">{details.subject}</Row> : null}
              {isInterpreting && Array.isArray(details.prepFiles) && details.prepFiles.length ? (
                <Row label="Documents préparatoires">
                  {details.prepFiles.map((f) => `${f.name}${f.words ? ` (~${Number(f.words).toLocaleString('fr-FR')} mots)` : ''}`).join(', ')}
                </Row>
              ) : null}
              <Row label="Priorité">{d.priority?.[order.priority] || PRIORITY_LABELS[order.priority] || order.priority}</Row>
              <Row label="Date limite">{order.deadline || '—'}</Row>
              {order.notes ? <Row label="Instructions">{order.notes}</Row> : null}
            </dl>
            </div>
          </section>

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Loader size={16} /></span>
              <h2>Progression</h2>
            </div>
            <div className="ah-card-body">
              <ol className="ord-steps">
                {STEP_LABELS.map((label, i) => {
                  const cur = STEP_FOR_STATUS[order.status] ?? 0;
                  const cancelled = CANCELLED_STATUSES.includes(order.status);
                  const cls = i < cur ? 'is-done' : i === cur && !cancelled ? 'is-current' : '';
                  return (
                    <li key={label} className={cls}>
                      <span className="ord-dot">{i < cur ? <Check size={13} /> : i + 1}</span>
                      {label}
                    </li>
                  );
                })}
              </ol>
            </div>
          </section>

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><FileText size={16} /></span>
              <h2>Documents</h2>
            </div>
            <div className="ah-card-body">
            {order.documents?.length > 0 ? (
              order.documents.map((doc) => (
                <div className="od-doc" key={doc.id || doc.name}>
                  <span className="od-doc-icon"><FileText size={18} /></span>
                  <span className="od-doc-body">
                    <strong>{doc.name}</strong>
                    <small>{DOC_KIND_LABELS[doc.kind] || doc.kind}</small>
                  </span>
                  <span className="od-doc-actions">
                    <a className="dash-link" href={fileUrl(doc.url)} target="_blank" rel="noreferrer">Visualiser</a>
                    <a className="dash-link" href={fileUrl(doc.url)} target="_blank" rel="noreferrer" download>Télécharger</a>
                  </span>
                </div>
              ))
            ) : <p className="ah-empty">Aucun document.</p>}
            </div>
          </section>

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Banknote size={16} /></span>
              <h2>Prix</h2>
            </div>
            <div className="ah-card-body">
            <ul className="dash-file-list od-price">
              {breakdown ? (
                Object.entries(breakdown).filter(([, v]) => Number(v)).map(([k, v]) => (
                  <li key={k}><span style={{ textTransform: 'capitalize' }}>{k}</span><strong>{v} {order.currency || 'TND'}</strong></li>
                ))
              ) : order.price != null ? (
                <li><span>Prestation</span><strong>{order.price} {order.currency || 'TND'}</strong></li>
              ) : (
                <li><span>Prestation</span><strong>Tarif à confirmer</strong></li>
              )}
              {Number(deliveryFee) > 0 && (
                <li><span>Livraison</span><strong>{deliveryFee} {order.currency || 'TND'}</strong></li>
              )}
              <li className="od-total"><span>Total</span><strong>{order.price != null ? `${order.price} ${order.currency || 'TND'}` : '—'}</strong></li>
              <li>
                <span>Paiement</span>
                <StatusBadge status={order.paymentStatus || 'unpaid'} labels={d.payment} />
              </li>
            </ul>
            {pendingTransfers.length > 0 && (
              <div style={{
                marginTop: 12, padding: '10px 12px', borderRadius: 10,
                background: '#fffbeb', border: '1px solid #fcd34d',
              }}>
                <p style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 6px', fontWeight: 600, fontSize: '0.85rem', color: '#92400e' }}>
                  <Landmark size={14} /> Virement à vérifier
                </p>
                {pendingTransfers.map((p) => (
                  <div key={p.id} style={{ marginBottom: 8 }}>
                    <p style={{ margin: '0 0 2px', fontSize: '0.82rem' }}>
                      Montant attendu : <strong>{p.amount} {p.currency || 'TND'}</strong>
                    </p>
                    <p style={{ margin: '0 0 6px', fontSize: '0.82rem' }}>
                      Référence : <code><strong>{p.reference || `TAR-${order.code}`}</strong></code>
                    </p>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="dash-btn-sm"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#047857' }}
                        onClick={() => setPayVerify({ type: 'confirm', payment: p })}
                      >
                        <Check size={12} /> Confirmer le paiement
                      </button>
                      <button
                        type="button"
                        className="dash-btn-sm"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b91c1c' }}
                        onClick={() => setPayVerify({ type: 'reject', payment: p })}
                      >
                        <XCircle size={12} /> Non reçu
                      </button>
                    </div>
                  </div>
                ))}
                <p style={{ display: 'flex', alignItems: 'flex-start', gap: 5, margin: '4px 0 0', fontSize: '0.75rem', color: '#92400e' }}>
                  <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                  Confirmez uniquement si le crédit est effectif sur le relevé bancaire.
                </p>
              </div>
            )}
            </div>
          </section>
        </div>

        <div className="ah-col">
          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><User size={16} /></span>
              <h2>Client</h2>
            </div>
            <div className="ah-card-body">
            <div className="od-pro">
              <span className="od-avatar">{(clientName || '?').charAt(0).toUpperCase()}</span>
              <div className="od-pro-body">
                <strong>{clientName}</strong>
                <small>{contact.email || order.clientEmail || '—'}</small>
                <small>{contact.phone || '—'}</small>
              </div>
            </div>
            {order.clientId && (
              <div className="dash-actions" style={{ marginTop: 12, marginBottom: 0 }}>
                <Link className="btn btn-outline" to={`/admin/clients/${order.clientId}`}>
                  Voir le profil client
                </Link>
              </div>
            )}
            </div>
          </section>

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><UserCheck size={16} /></span>
              <h2>Professionnel affecté</h2>
            </div>
            <div className="ah-card-body">
            {order.translator ? (
              <>
                <div className="od-pro">
                  <span className="od-avatar">{(order.translator || '?').charAt(0).toUpperCase()}</span>
                  <div className="od-pro-body">
                    <strong>{order.translator}</strong>
                    <small>{order.sourceLang} → {order.targetLang}{details.translationService ? ` · ${details.translationService}` : ''}</small>
                    <small><span className="avail-pill avail-pill--available">Actif</span></small>
                  </div>
                </div>
                <div className="od-meta">
                  <div>
                    <em>Affectation</em>
                    <strong>{order.assignmentMethod === 'auto' ? 'Automatique' : 'Manuelle'}</strong>
                  </div>
                  <div>
                    <em>Date</em>
                    <strong>{order.assignedAt || '—'}</strong>
                  </div>
                </div>
                <div className="dash-actions" style={{ marginTop: 12, marginBottom: 0 }}>
                  {order.translatorId && (
                    <Link className="btn btn-outline" to={`/admin/translators/${order.translatorId}`}>
                      Voir profil
                    </Link>
                  )}
                  {!closed && (
                    <>
                      <button type="button" className="btn btn-outline" onClick={() => setAssignOpen(true)}>
                        Changer
                      </button>
                      <button type="button" className="btn btn-outline" onClick={unassign}>
                        Désaffecter
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="order-card-await">
                <span><strong>⚠️ Aucun professionnel affecté</strong></span>
              </div>
            )}
            </div>
          </section>

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Truck size={16} /></span>
              <h2>Livraison</h2>
            </div>
            <div className="ah-card-body">
            <dl className="od-dl">
              <Row label="Mode">{DELIVERY_MODE_LABELS[order.delivery] || order.delivery || '—'}</Row>
              <Row label="Email">{contact.email || order.clientEmail || '—'}</Row>
              {order.deliveryInfo?.recipient?.name ? <Row label="Destinataire">{order.deliveryInfo.recipient.name}{order.deliveryInfo.recipient.phone ? ` · ${order.deliveryInfo.recipient.phone}` : ''}</Row> : null}
              {order.deliveryInfo?.address ? <Row label="Adresse">{order.deliveryInfo.address}</Row> : null}
              {order.deliveryInfo?.recipient ? (
                <Row label="Ville / Pays">
                  {[
                    [order.deliveryInfo.recipient.postalCode, order.deliveryInfo.recipient.city].filter(Boolean).join(' '),
                    order.deliveryInfo.recipient.country,
                  ].filter(Boolean).join(', ') || '—'}
                </Row>
              ) : null}
              {order.deliveryInfo?.trackingNumber ? <Row label="Suivi">{order.deliveryInfo.trackingNumber}</Row> : null}
              <Row label="Statut">{deliveryStatus(order)}</Row>
            </dl>
            </div>
          </section>

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><History size={16} /></span>
              <h2>Historique</h2>
            </div>
            <div className="ah-card-body">
            {(order.history || []).length > 0 ? (
              <ol className="od-timeline">
                {[...order.history].reverse().map((h, i) => (
                  <li key={`${h.at}-${i}`}>
                    <span className="ot-time">
                      {h.at ? new Date(h.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </span>
                    <span className="ot-label">
                      {HISTORY_LABELS[h.action] || h.action}
                      {h.detail ? ` — ${h.detail}` : ''}
                    </span>
                    <span className="ot-by">par {h.by}</span>
                  </li>
                ))}
              </ol>
            ) : <p className="ah-empty">Aucun événement.</p>}
            </div>
          </section>
        </div>
      </div>

      <AssignTranslatorForm
        order={order}
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onDone={reload}
      />

      <DashModal
        open={Boolean(payVerify)}
        title={payVerify?.type === 'confirm' ? 'Confirmer le paiement' : 'Virement non reçu'}
        subtitle={payVerify ? `${order.code} — ${payVerify.payment.amount} ${payVerify.payment.currency || 'TND'}` : ''}
        onClose={() => !payBusy && setPayVerify(null)}
      >
        {payVerify && (
          <div style={{ padding: '4px 2px' }}>
            {payVerify.type === 'confirm' ? (
              <p style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', fontSize: '0.88rem' }}>
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  Confirmez uniquement si le crédit est <strong>effectif sur le relevé bancaire</strong>
                  {' '}(montant {payVerify.payment.amount} {payVerify.payment.currency || 'TND'},
                  référence <code>{payVerify.payment.reference || `TAR-${order.code}`}</code>).
                  Une capture ou un justificatif du client ne suffit pas.
                </span>
              </p>
            ) : (
              <p style={{ fontSize: '0.9rem' }}>
                Le virement annoncé n’a pas été crédité ? Le paiement sera marqué <strong>échoué</strong>
                {' '}et le client sera notifié pour régler à nouveau sa commande.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button type="button" className="btn btn-outline" onClick={() => setPayVerify(null)} disabled={payBusy}>
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={payVerify.type === 'reject' ? { color: '#b91c1c', borderColor: '#fca5a5', background: '#fff' } : undefined}
                onClick={runPayVerify}
                disabled={payBusy}
              >
                {payBusy ? 'Traitement…' : payVerify.type === 'confirm' ? 'Crédit vérifié — confirmer' : 'Marquer non reçu'}
              </button>
            </div>
          </div>
        )}
      </DashModal>

      {msg && (
        <div className="dash-toast dash-toast--float" role="status">
          <span>{msg}</span>
          <button type="button" onClick={() => setMsg('')} aria-label="Fermer">
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
